import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import MuiButton from "@material-ui/core/Button";
import CustomTabs from "components/CustomTabs/CustomTabs.js";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import ReactTable from "components/ReactTable/ReactTable";
import { MessageOutlined, RemoveRedEye, Close } from "@material-ui/icons";

const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

const styles = {
  searchWrapper: {
    marginTop: "10px",
  },
  dialogTitleRoot: {
    padding: "0",
  },
  dialogHeader: {
    backgroundColor: "#6B9D94",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 20px",
  },
  dialogHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  dialogContent: {
    padding: "24px !important",
  },
  dialogActions: {
    padding: "16px 24px 24px 24px",
    display: "flex",
    justifyContent: "flex-start",
    gap: "10px",
    flexWrap: "wrap",
  },
};

const useStyles = makeStyles(styles);

const STATS_API_URL =
  "http://3.147.61.51:8000/api/v1/stats?page=1&page_size=5000";

const EMPTY_OVERALL = {
  total_books: 0,
  avg_completion: 0,
  avg_minutes_per_day: 0,
  avg_wpm: 0,
  avg_comprehension: 0,
  avg_fluency: 0,
};

export default function UserProfile() {
  const classes = useStyles();

  const [allStudents, setAllStudents] = useState([]);
  const [allIndependentReaders, setAllIndependentReaders] = useState([]);
  const [allAdmin, setAllAdmin] = useState([]);
  const [allParents, setAllParents] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allOrganizations, setAllOrganizations] = useState([]);

  const [allStats, setAllStats] = useState([]);
  const [loadingAllStats, setLoadingAllStats] = useState(false);

  const [openMessage, setOpenMessage] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState({
    first_name: "",
    last_name: "",
    email: "",
    group: "",
  });
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  const [openReport, setOpenReport] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [selectedStudentForReport, setSelectedStudentForReport] = useState({
    student_id: null,
    login_name: "",
    first_name: "",
    last_name: "",
    books: [],
    overall: { ...EMPTY_OVERALL },
  });

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const formatBookTitle = (value) => {
    if (!value) return "N/A";
    return String(value).replace(/\.pdf$/i, "");
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";

    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const resetReportState = () => {
    setSelectedStudentForReport({
      student_id: null,
      login_name: "",
      first_name: "",
      last_name: "",
      books: [],
      overall: { ...EMPTY_OVERALL },
    });
  };

  const handleOpenMessage = (student) => {
    setSelectedStudent({
      first_name: student.student_first_name || "",
      last_name: student.student_last_name || "",
      email: student.student_email || "",
      group: student.product_code || "",
    });

    setMessageText("");
    setOpenMessage(true);
  };

  const fetchAllStats = async () => {
    try {
      setLoadingAllStats(true);
      const response = await axios.get(STATS_API_URL);
      const items = Array.isArray(response?.data?.items) ? response.data.items : [];
      setAllStats(items);
      return items;
    } catch (error) {
      console.error("Error fetching stats:", error);
      setAllStats([]);
      toast.error("Failed to load reading stats.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return [];
    } finally {
      setLoadingAllStats(false);
    }
  };

  const handleViewReport = async (student) => {
    setOpenReport(true);
    setLoadingReport(true);

    const studentId = Number(student.student_id);
    const loginName = String(
      student.login_name || student.student_login || ""
    )
      .trim()
      .toLowerCase();

    try {
      let statsSource = allStats;

      if (!statsSource || statsSource.length === 0) {
        statsSource = await fetchAllStats();
      }

      const filteredStats = statsSource
        .filter((item) => {
          const statStudentId = Number(item?.student_id);
          const statLoginName = String(
            item?.student_login || item?.login_name || ""
          )
            .trim()
            .toLowerCase();

          const matchByStudentId =
            Number.isInteger(studentId) &&
            studentId > 0 &&
            Number.isInteger(statStudentId) &&
            statStudentId === studentId;

          const matchByLogin =
            !!loginName && !!statLoginName && statLoginName === loginName;

          return matchByStudentId || matchByLogin;
        })
        .sort((a, b) => {
          const dateA = new Date(a?.updated_at || a?.stats_date || 0).getTime();
          const dateB = new Date(b?.updated_at || b?.stats_date || 0).getTime();
          return dateB - dateA;
        });

      const books = filteredStats.map((item, index) => ({
        id: `${item.id || index}-${item.student_id || item.login_name || item.student_login || "student"}`,
        book_filename: item.book_filename || `book-${index + 1}`,
        book_title: formatBookTitle(item.book_title || item.book_filename),
        completion_percent: toNumber(item.completion_percent),
        minutes_per_day: toNumber(
          item.minutes_today ?? item.minutes_per_day ?? item.minutes
        ),
        wpm: toNumber(item.wpm),
        comprehension: toNumber(
          item.average_quiz_score ?? item.comprehension ?? item.quiz_score
        ),
        fluency_score: toNumber(item.fluency_score),
      }));

      const totalBooks = books.length;

      const overall =
        totalBooks > 0
          ? {
              total_books: totalBooks,
              avg_completion:
                books.reduce(
                  (sum, book) => sum + toNumber(book.completion_percent),
                  0
                ) / totalBooks,
              avg_minutes_per_day:
                books.reduce(
                  (sum, book) => sum + toNumber(book.minutes_per_day),
                  0
                ) / totalBooks,
              avg_wpm:
                books.reduce((sum, book) => sum + toNumber(book.wpm), 0) /
                totalBooks,
              avg_comprehension:
                books.reduce(
                  (sum, book) => sum + toNumber(book.comprehension),
                  0
                ) / totalBooks,
              avg_fluency:
                books.reduce(
                  (sum, book) => sum + toNumber(book.fluency_score),
                  0
                ) / totalBooks,
            }
          : { ...EMPTY_OVERALL };

      setSelectedStudentForReport({
        student_id: Number.isInteger(studentId) ? studentId : null,
        login_name: loginName,
        first_name: student.student_first_name || "",
        last_name: student.student_last_name || "",
        books,
        overall,
      });
    } catch (error) {
      console.error("Error preparing report:", error);
      toast.error("Failed to load reading report.", {
        position: toast.POSITION.TOP_CENTER,
      });

      setSelectedStudentForReport({
        student_id: Number.isInteger(studentId) ? studentId : null,
        login_name: loginName,
        first_name: student.student_first_name || "",
        last_name: student.student_last_name || "",
        books: [],
        overall: { ...EMPTY_OVERALL },
      });
    } finally {
      setLoadingReport(false);
    }
  };

  const onCloseMessage = () => {
    if (sendingMessage) return;

    setOpenMessage(false);
    setMessageText("");
    setSelectedStudent({
      first_name: "",
      last_name: "",
      email: "",
      group: "",
    });
  };

  const onCloseReport = () => {
    setOpenReport(false);
    setLoadingReport(false);
    resetReportState();
  };

  const handleSendMessage = async (scope) => {
    const cleanMessage = String(messageText || "").trim();

    if (!cleanMessage) {
      toast.error("Please enter a message first.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    if (scope === "one" && !selectedStudent.email) {
      toast.error("Selected student email is missing.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    if (new Date() > new Date(currentUser.expiresInTime)) {
      toast.error("Unauthorized User. Please log In", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("user");
      window.location.href = "/";
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + currentUser.accessToken,
    };

    const payload =
      scope === "one"
        ? {
            email: selectedStudent.email,
            message: cleanMessage,
          }
        : {
            message: cleanMessage,
          };

    try {
      setSendingMessage(true);

      const response = await axios.post(
        process.env.REACT_APP_API_URL + `eop/messages/${scope}`,
        payload,
        { headers }
      );

      const apiData = response?.data || {};

      if (
        response.status >= 200 &&
        response.status < 300 &&
        (apiData.status === "success" ||
          apiData.code === 201 ||
          apiData.code === 200)
      ) {
        toast.success(
          scope === "one"
            ? `Message sent to ${selectedStudent.first_name} ${selectedStudent.last_name}`
            : "Message sent to all students",
          {
            position: toast.POSITION.TOP_CENTER,
          }
        );

        onCloseMessage();
        return;
      }

      toast.error(apiData.message || "Failed to send message.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } catch (error) {
      console.error("Send message error:", error);

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to send message";

      toast.error(backendMessage, {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setSendingMessage(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Organization",
        accessor: "organization",
      },
      {
        Header: "Role",
        accessor: "role",
      },
    ],
    []
  );

  const columns2 = useMemo(
    () => [
      {
        Header: "First Name",
        accessor: "student_first_name",
      },
      {
        Header: "Last Name",
        accessor: "student_last_name",
      },
      {
        Header: "Email",
        accessor: "student_email",
      },
      {
        Header: "Product",
        accessor: "product_code",
      },
      {
        Header: "Current Title",
        accessor: "title",
      },
      {
        Header: "Author",
        accessor: "author",
      },
      {
        Header: "Start",
        accessor: "start_date",
      },
      {
        Header: "MSG",
        accessor: "message",
        Cell: ({ row }) => (
          <MessageOutlined
            style={{ color: "grey", cursor: "pointer" }}
            onClick={() => handleOpenMessage(row.original)}
          />
        ),
      },
      {
        Header: "MRR",
        accessor: "mrr",
        Cell: ({ row }) => (
          <RemoveRedEye
            style={{
              color: loadingAllStats ? "#A0A0A0" : "grey",
              cursor: "pointer",
            }}
            onClick={() => handleViewReport(row.original)}
          />
        ),
      },
    ],
    [loadingAllStats]
  );

  const getAllUsers = () => {
    if (new Date() > new Date(currentUser.expiresInTime)) {
      toast.error("Unautorized User. Please log In", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("user");
      window.location.href = "/";
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + currentUser.accessToken,
    };

    fetch(process.env.REACT_APP_API_URL + `users/${currentUser.organization}`, {
      headers,
    })
      .then((res) => res.json())
      .then((data1) => {
        const arr = [];
        const admin = [];
        const student = [];
        const parent = [];
        const teacher = [];
        const allOrga = [];
        const data = data1.data || [];

        if (
          currentUser.role === "organization" ||
          currentUser.role === "admin" ||
          currentUser.role === "teacher"
        ) {
          for (let i = 0; i < data.length; i++) {
            if (
              data[i].role === "student" &&
              data[i].organization === currentUser.organization
            ) {
              student.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
              arr.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
            } else if (
              data[i].role === "teacher" &&
              data[i].organization === currentUser.organization
            ) {
              teacher.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
              arr.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
            } else if (
              data[i].role === "parent" &&
              data[i].organization === currentUser.organization
            ) {
              parent.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
              arr.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
            } else if (
              (data[i].role === "admin" || data[i].role === "organization") &&
              data[i].organization === currentUser.organization
            ) {
              admin.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
              arr.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
            }
          }
        } else if (
          currentUser.role === "student" ||
          currentUser.role === "parent"
        ) {
          for (let i = 0; i < data.length; i++) {
            if (
              data[i].role === "student" &&
              data[i].organization === currentUser.organization
            ) {
              student.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
              arr.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
            } else if (
              data[i].role === "teacher" &&
              data[i].organization === currentUser.organization
            ) {
              teacher.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
              arr.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
            }
          }
        } else {
          for (let i = 0; i < data.length; i++) {
            if (data[i].role === "student") {
              student.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
            } else if (data[i].role === "teacher") {
              teacher.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
            } else if (data[i].role === "parent") {
              parent.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
            } else if (
              data[i].role === "admin" ||
              data[i].role === "organization"
            ) {
              admin.push({
                email: data[i].email,
                organization: data[i].organization,
                role: data[i].role,
              });
              allOrga.push({
                value: data[i].organization,
                label: data[i].organization,
              });
            }

            arr.push({
              email: data[i].email,
              organization: data[i].organization,
              role: data[i].role,
            });
          }
        }

        setAllUsers(arr);
        setAllStudents(student);
        setAllTeachers(teacher);
        setAllAdmin(admin);
        setAllParents(parent);
        setAllOrganizations(allOrga);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  };

  const getIndependentReaders = () => {
    if (new Date() > new Date(currentUser.expiresInTime)) {
      toast.error("Unauthorized User. Please log In", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("user");
      window.location.href = "/";
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + currentUser.accessToken,
    };

    fetch(process.env.REACT_APP_API_URL + `eop/users/latest-bookmarks`, {
      headers,
    })
      .then((res) => res.json())
      .then((data1) => {
        const arr = [];
        const data = data1.data || [];

        for (let i = 0; i < data.length; i++) {
          const latestBookmark = data[i].latestBookmark || {};

          arr.push({
            student_id: data[i].student_id ? Number(data[i].student_id) : null,
            login_name:
              data[i].login_name ||
              data[i].student_login ||
              data[i].username ||
              latestBookmark.login_name ||
              "",
            student_email: data[i].student_email || data[i].email || "",
            student_first_name:
              data[i].student_first_name || data[i].first_name || "",
            student_last_name:
              data[i].student_last_name || data[i].last_name || "",
            product_code: data[i].product_code || "",
            title: latestBookmark.book_filename
              ? formatBookTitle(latestBookmark.book_filename)
              : data[i].title || "N/A",
            author: data[i].author || "N/A",
            start_date: formatDate(latestBookmark.created_at || data[i].created_at),
            end_date: data[i].end_date || "N/A",
          });
        }

        setAllIndependentReaders(arr);
      })
      .catch((error) => {
        console.error("Error fetching independent readers:", error);
      });
  };

  useEffect(() => {
    getAllUsers();
    getIndependentReaders();
    fetchAllStats();
  }, []);

  return (
    <div>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <CustomTabs
            title=""
            headerColor="primary"
            tabs={[
              {
                tabName: "All Users",
                tabContent: (
                  <div className={classes.searchWrapper}>
                    <ReactTable columns={columns} data={allUsers} />
                  </div>
                ),
              },
              {
                tabName: "Independent Readers",
                tabContent: (
                  <div className={classes.searchWrapper}>
                    <ReactTable columns={columns2} data={allIndependentReaders} />
                  </div>
                ),
              },
            ]}
          />
        </GridItem>
      </GridContainer>

      <Dialog
        open={openMessage}
        onClose={onCloseMessage}
        fullWidth
        maxWidth="md"
        aria-labelledby="send-message-dialog-title"
      >
        <DialogTitle
          id="send-message-dialog-title"
          className={classes.dialogTitleRoot}
        >
          <div className={classes.dialogHeader}>
            <div className={classes.dialogHeaderLeft}>
              <Typography variant="h6" style={{ color: "#fff", fontWeight: 600 }}>
                Send Message
              </Typography>
            </div>

            <IconButton onClick={onCloseMessage} style={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 700, marginBottom: 8 }}
          >
            SEND MESSAGE TO: {selectedStudent.first_name} {selectedStudent.last_name}
          </Typography>

          <Typography
            variant="body2"
            style={{ marginBottom: 16, color: "#666" }}
          >
            {selectedStudent.email}
          </Typography>

          <TextField
            label="Compose Message"
            placeholder="Type your message here..."
            multiline
            rows={6}
            variant="outlined"
            fullWidth
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            disabled={sendingMessage}
          />
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <MuiButton
            variant="contained"
            style={{ backgroundColor: "#F2B500", color: "#000" }}
            onClick={() => handleSendMessage("one")}
            disabled={sendingMessage}
          >
            {sendingMessage ? "Sending..." : "Send to This Student"}
          </MuiButton>

          <MuiButton
            variant="contained"
            style={{ backgroundColor: "#F2B500", color: "#000" }}
            onClick={() => handleSendMessage("all")}
            disabled={sendingMessage}
          >
            {sendingMessage ? "Sending..." : "Send to All Students"}
          </MuiButton>
        </DialogActions>
      </Dialog>

<Dialog
  open={openReport}
  onClose={onCloseReport}
  fullWidth
  maxWidth="xl"
  aria-labelledby="reading-report-dialog-title"
>
  <DialogTitle
    id="reading-report-dialog-title"
    className={classes.dialogTitleRoot}
  >
    <div className={classes.dialogHeader}>
      <div className={classes.dialogHeaderLeft}>
        <Typography variant="h6" style={{ color: "#fff", fontWeight: 600 }}>
          Reading Report
          {selectedStudentForReport.first_name ||
          selectedStudentForReport.last_name
            ? ` - ${selectedStudentForReport.first_name} ${selectedStudentForReport.last_name}`
            : ""}
        </Typography>
      </div>
      <IconButton onClick={onCloseReport} style={{ color: "#fff" }}>
        <Close />
      </IconButton>
    </div>
  </DialogTitle>

  <DialogContent style={{ padding: 0, overflow: "hidden" }}>
    <div style={{ width: "100%", padding: "16px" }}>
      {loadingReport ? (
        <div
          style={{
            height: "72vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            background: "#fff",
          }}
        >
          <Typography variant="h6" style={{ color: "#666", fontWeight: 600 }}>
            Loading reading data...
          </Typography>
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            overflow: "hidden",
            background: "#fff",
            height: "72vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* HEADER FIXE */}
          <div style={{ flexShrink: 0 }}>
            <table
              style={{
                width: "100%",
                tableLayout: "fixed",
                borderCollapse: "collapse",
              }}
            >
              <colgroup>
                <col style={{ width: "30%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>
              <thead>
                <tr style={{ backgroundColor: "#6B9D94" }}>
                  <th className="px-4 py-4 text-left font-bold" style={{ color: "#000", fontSize: "16px" }}>
                    Book title
                  </th>
                  <th className="px-4 py-4 text-center font-bold" style={{ color: "#000", fontSize: "16px" }}>
                    Completion
                  </th>
                  <th className="px-4 py-4 text-center font-bold" style={{ color: "#000", fontSize: "16px" }}>
                    Minutes/Day
                  </th>
                  <th className="px-4 py-4 text-center font-bold" style={{ color: "#000", fontSize: "16px" }}>
                    Words/minute
                  </th>
                  <th className="px-4 py-4 text-center font-bold" style={{ color: "#000", fontSize: "16px" }}>
                    Comprehension
                  </th>
                  <th className="px-4 py-4 text-center font-bold" style={{ color: "#000", fontSize: "16px" }}>
                    Overall Fluency
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          {/* BODY SCROLLABLE */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                tableLayout: "fixed",
                borderCollapse: "collapse",
              }}
            >
              <colgroup>
                <col style={{ width: "30%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>
              <tbody>
                {selectedStudentForReport.books.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      No reading data yet. Start reading a book to see your report.
                    </td>
                  </tr>
                ) : (
                  selectedStudentForReport.books.map((book, index) => (
                    <tr
                      key={book.id}
                      className={`border-b border-gray-200 ${
                        index % 2 === 0 ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-3 text-left break-words">
                        {book.book_title}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {book.completion_percent.toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-center">
                        {book.minutes_per_day.toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {book.wpm.toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {book.comprehension.toFixed(1)}%
                      </td>
                      <td
                        className="px-4 py-3 text-center font-semibold"
                        style={{ color: "#EF4444" }}
                      >
                        {book.fluency_score.toFixed(1)}%
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* OVERALL FIXE EN BAS */}
          <div
            style={{
              flexShrink: 0,
              borderTop: "3px solid #94a3b8",
              background: "#ffffff",
              position: "relative",
              zIndex: 20,
              boxShadow: "0 -8px 22px rgba(0,0,0,0.16)",
            }}
          >
            <table
              style={{
                width: "100%",
                tableLayout: "fixed",
                borderCollapse: "collapse",
              }}
            >
              <colgroup>
                <col style={{ width: "30%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>
              <tbody>
                <tr style={{ backgroundColor: "#6B9D94" }}>
                  <th className="px-4 py-3 text-left font-semibold text-white"></th>
                  <th className="px-4 py-3 text-center font-semibold text-white"></th>
                  <th className="px-4 py-3 text-center font-semibold text-white"></th>
                  <th className="px-4 py-3 text-center font-semibold text-white"></th>
                  <th className="px-4 py-3 text-center font-semibold text-white"></th>
                  <th className="px-4 py-3 text-center font-semibold text-white"></th>
                </tr>

                <tr
                  style={{
                    background: "#f8fafc",
                    color: "#111827",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <th className="px-4 py-4 text-left font-extrabold" style={{ fontSize: "17px" }}>
                    Totals book
                  </th>
                  <th className="px-4 py-4 text-center font-extrabold" style={{ fontSize: "17px" }}>
                    Completion Average
                  </th>
                  <th className="px-4 py-4 text-center font-extrabold" style={{ fontSize: "17px" }}>
                    Minutes/Day Average
                  </th>
                  <th className="px-4 py-4 text-center font-extrabold" style={{ fontSize: "17px" }}>
                    Speed Average
                  </th>
                  <th className="px-4 py-4 text-center font-extrabold" style={{ fontSize: "17px" }}>
                    Comp Average
                  </th>
                  <th className="px-4 py-4 text-center font-extrabold" style={{ fontSize: "17px" }}>
                    Fluency Average
                  </th>
                </tr>

                <tr style={{ background: "#ffffff", color: "#111827" }}>
                  <th className="px-4 py-5 text-left font-extrabold" style={{ fontSize: "24px" }}>
                    {selectedStudentForReport.overall?.total_books ?? 0}
                  </th>
                  <th
                    className="px-4 py-5 text-center font-extrabold"
                    style={{ fontSize: "24px", color: "#2563eb" }}
                  >
                    {(selectedStudentForReport.overall?.avg_completion ?? 0).toFixed(1)}%
                  </th>
                  <th
                    className="px-4 py-5 text-center font-extrabold"
                    style={{ fontSize: "24px", color: "#2563eb" }}
                  >
                    {(selectedStudentForReport.overall?.avg_minutes_per_day ?? 0).toFixed(1)}
                  </th>
                  <th
                    className="px-4 py-5 text-center font-extrabold"
                    style={{ fontSize: "24px", color: "#2563eb" }}
                  >
                    {(selectedStudentForReport.overall?.avg_wpm ?? 0).toFixed(1)}
                  </th>
                  <th
                    className="px-4 py-5 text-center font-extrabold"
                    style={{ fontSize: "24px", color: "#2563eb" }}
                  >
                    {(selectedStudentForReport.overall?.avg_comprehension ?? 0).toFixed(1)}%
                  </th>
                  <th
                    className="px-4 py-5 text-center font-extrabold"
                    style={{ fontSize: "24px", color: "#ef4444" }}
                  >
                    {(selectedStudentForReport.overall?.avg_fluency ?? 0).toFixed(1)}%
                  </th>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  </DialogContent>

  <DialogActions className={classes.dialogActions}>
    <MuiButton
      variant="contained"
      style={{ backgroundColor: "#6B9D94", color: "#fff" }}
      onClick={onCloseReport}
    >
      Close
    </MuiButton>
  </DialogActions>
</Dialog>

      <ToastContainer />
    </div>
  );
}